import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Crown, AlertTriangle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface InstantAccountInfoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
  botName: string;
}

export const InstantAccountInfoDialog = ({
  isOpen,
  onClose,
  onContinue,
  botName
}: InstantAccountInfoDialogProps) => {
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Crown className="h-6 w-6 text-amber-500" />
            <DialogTitle className="text-amber-700 dark:text-amber-300">
              Instant Account Prop Bot
            </DialogTitle>
          </div>
          <DialogDescription>
            You're about to start trading with <strong>{botName}</strong>, 
            an instant account prop trading bot.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-700 dark:text-amber-300">
              <strong>Live Accounts Only:</strong> This bot only accepts live trading accounts.
              Demo accounts cannot be connected to instant account prop bots.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <h4 className="font-semibold text-sm flex items-center gap-1">
              <CheckCircle className="h-4 w-4 text-emerald-500" />
              Benefits of Instant Account Bots:
            </h4>
            <ul className="text-sm text-muted-foreground space-y-1 ml-5">
              <li>• Real capital from prop firms</li>
              <li>• Higher profit potential</li>
              <li>• Professional trading environment</li>
              <li>• Direct access to live markets</li>
            </ul>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button 
              onClick={onContinue} 
              className="flex-1 bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white border-0"
            >
              Continue
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};